/* eslint-disable no-redeclare */
import {
  Bool,
  SegmentType,
  SourceSegmentIdBizType,
} from '@ve-pro/enums/segment'
import {
  FontStyle,
  FontWeight,
  TextDecoration,
  TextAlign,
  TextResize,
} from '@ve-pro/enums/text-property'
import type {
  Segment,
  SegmentAudio,
  SegmentCollection,
  SegmentFilter,
  SegmentMaterial,
  SegmentPositionSize,
  SegmentSourceRate,
  SegmentSticker,
  SegmentText,
  SegmentTransition,
  SegmentVideo,
  SegmentTextProperty,
  SegmentMattingProperty,
  SegmentGroupCollection,
  SegmentNoGroupCollection,
  RatioProperties,
  SegmentRatios,
  SegmentFrame,
  SegmentRateCollection,
  SegmentFragment,
  SegmentSubtitleGroup,
} from '@ve-pro/types/track.type'
import { SegmentAssetMap } from './segment-asset-map'
import { AssetSource, AssetType } from '@ve-pro/enums/asset'
import { cloneDeep } from 'lodash-es'
import { FontModel } from '@ve-pro/api/aside/type'
import { useProjectStore } from '../project'
import {
  getExcludeTransitionDurationFrames,
  getTransitionDurationMax,
  getTransitionOverlap,
  isTransition,
  TRANSITION_DURATION,
} from './segment/segment-transition'
import { useTrackStore } from '.'
import { t } from '@ve-pro/ve/lang'
import {
  getGroupBySegment,
  getGroupChildSegments,
  isSegmentGroup,
  isSegmentGroupChild,
  isSupportDriftSpeed,
} from './segment/segment-group'
import { DEFAULT_FONT_FAMILY as FONT_FAMILY } from 'video-editor-renderer'
import { isSupportMoreRatio } from './segment/segment-position'
import { ratioEffectKeys } from './segment/segment-ratio'
import Vue from 'vue'
import { durationToFrames, framesToDuration, getFullFrameMs } from './frame'
import { getSegmentRateMax, isSupportRate } from './segment/segment-rate'
import { add } from '@com/utils/math'
import { isSegmentFragment } from './segment/segment-fragment'
import { defineSegmentBehavior } from './segment/segment-behavior'
import { isSegmentDubbing } from './segment/segment-subtitle'
import { ProjectMode } from '@ve-pro/enums/project'
import { isLangSegment, isTemplateSegment } from './tracks-actions'

// 片段默认时长
export const Fragment_DURATION = 100
// 文本默认时长
export const TEXT_DURATION = 3000
// 图片默认时长
export const IMAGE_DURATION = 3000
// 贴图默认时长
const STICKER_DURATION = 3000
// 滤镜默认时长
const FILTER_DURATION = 3000
// 滤镜默认的强度
export const FILTER_UNI_ALPHA = 100
// 字幕的字体默认大小
export const SUBTITLE_DEFAULT_FONT_SIZE = 60
export const DEFAULT_FONT_SIZE = 150
export const DEFAULT_FONT_HEIGHT = 248
// 竖版坑位的字幕大小
export const V_SUBTITLE_DEFAULT_FONT_SIZE = 64
// 横版坑位的字幕大小
export const H_SUBTITLE_DEFAULT_FONT_SIZE = 36

// 字幕默认背景颜色
export const DEFAULT_BACKGROUND_COLOR = '#000000'
export const DEFAULT_BACKGROUND_OPACITY = 65

export const segmentPositionSize: SegmentPositionSize = {
  x: 0,
  y: 0,
  scale_x: 100,
  scale_y: 100,
  rotation: 0,
  width: 0,
  height: 0,
}

export const TEXT_PROPERTY = (): SegmentTextProperty => ({
  text: t('material.text.default'),
  color: '#ffffff',
  font_size: DEFAULT_FONT_SIZE,
  font_style: FontStyle.Normal,
  font_weight: FontWeight.Normal,
  text_decoration: TextDecoration.Normal,
  line_spacing: 0,
  letter_spacing: 0,
  text_align: TextAlign.Center,
  text_resize: TextResize.AutoWidth,
  text_stroke_width: 0,
  text_stroke_color: '',
  background_color: '',
  background_opacity: 0,
  // shadow
  shadow_color: '',
  shadow_blur: 0,
  shadow_x: 0,
  shadow_y: 0,
  flower_style: null,
  auto_font_size: false,
})

export const DEFAULT_TEXT_PROPERTY = (
  isSubtitleMode = false,
): SegmentTextProperty => {
  const projectStore = useProjectStore()

  const property = TEXT_PROPERTY()

  if (isSubtitleMode || projectStore.isSubtitleMode) {
    property.font_size = SUBTITLE_DEFAULT_FONT_SIZE
    property.text_resize = TextResize.AutoWidthHeight
    property.background_color = DEFAULT_BACKGROUND_COLOR
    property.background_opacity = DEFAULT_BACKGROUND_OPACITY
  }

  return property
}

const resolveRatioProperty = (): RatioProperties => {
  return {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    scale_x: 100,
    scale_y: 100,
    mask: null,
  }
}

export const segmentMatting: SegmentMattingProperty = {
  enable_matting: true,
  matting_color: '',
  matting_strength: 100,
  matting_feather: 1,
}

export const DEFAULT_FONT_FAMILY: FontModel['value'] = FONT_FAMILY
export type ResolveMaterialSegmentReturnType = Exclude<
  SegmentCollection,
  | SegmentText
  | SegmentSubtitleGroup
  | SegmentTransition
  | SegmentSticker
  | SegmentFragment
> | null

// 判断是否图片的SegmentVideo
export function isSegmentVideoImage(segment: SegmentCollection): boolean {
  return segment.type === SegmentType.Video && isImage(segment.material.type)
}

export function resolveSegment(
  material: SegmentMaterial,
): ResolveMaterialSegmentReturnType {
  // 保证数据的不污染
  material = cloneDeep(material)

  // 注意：资源是图片类型，并且是从公共素材库过来的资源
  const isSticker =
    material.type === AssetType.Image &&
    (typeof material.source === 'undefined' ||
      material.source === AssetSource.Public)

  const isVideoImage =
    [AssetType.Image, AssetType.Animation].includes(material.type) &&
    (material.source === AssetSource.User ||
      material.source === AssetSource.Asset)

  let type: SegmentType

  if (isSticker) {
    type = SegmentType.Sticker
  } else if (isVideoImage) {
    type = SegmentType.Video
  } else {
    type = SegmentAssetMap[material.type]
  }

  if (type === SegmentType.Sticker) {
    type = SegmentType.Video
  }

  switch (type) {
    case SegmentType.Filter:
      return resolveSegmentFilter(material)
    case SegmentType.Video:
      return resolveSegmentVideo(material)
    case SegmentType.Audio:
      return resolveSegmentAudio(material)
    default:
      return null
  }
}

function resolveBaseSegment(material: SegmentMaterial): Segment
function resolveBaseSegment(
  type: SegmentType.Text | SegmentType.Fragment,
): Segment
function resolveBaseSegment(
  materialOrSegmentText:
    | SegmentMaterial
    | SegmentType.Text
    | SegmentType.Fragment,
): Segment {
  const projectStore = useProjectStore()
  let material: SegmentMaterial | undefined
  let type: Segment['type']

  if (
    materialOrSegmentText === SegmentType.Text ||
    materialOrSegmentText === SegmentType.Fragment
  ) {
    type = materialOrSegmentText
  } else {
    material = materialOrSegmentText
    type = SegmentAssetMap[materialOrSegmentText.type]
  }

  const materialDuration = getFullFrameMs(material?.duration ?? 0, {
    rounding: 'floor',
  })

  return {
    stage: projectStore.mode,
    pid: 0,
    id: -1,
    asset_id: type === SegmentType.Text ? 0 : material?.asset_id ?? 0,
    asset_type: material?.type ?? AssetType.Text,
    res_id: 0,
    type,
    name: material?.name ?? '',
    track_id: '',
    start_time: 0,
    trim_start_time: 0,
    duration: materialDuration,

    // support for frame
    start_frame: 0,
    trim_start_frame: 0,
    duration_frames: 0,

    rate: 1,
    source_rate: {
      material_duration: materialDuration,
      trim_start_time: 0,
      duration: materialDuration,
    },
    lock_scale: false, // 锁定缩放比例开关默认关闭：
    alpha: 100,
    deprecated: false,
    disabled: false,
    behavior: {},
    drift_speed: 0,
    multi_cross: false,
    lang: '',
    source_segment_id: 0,
    biz_type: SourceSegmentIdBizType.Default,
    fragment_segment_id: 0,
  }
}

export function replaceMaterial<
  T extends Exclude<
    SegmentCollection,
    SegmentText | SegmentSubtitleGroup | SegmentFragment
  >,
>(segment: T, material: SegmentMaterial): T {
  segment.name = material.name
  segment.asset_id = material.asset_id

  segment.material = material
  return segment
}

export function resolveSegmentText(
  material?: SegmentMaterial,
  isSubtitleMode = false,
): SegmentText {
  const segment = resolveBaseSegment(SegmentType.Text)
  const projectStore = useProjectStore()

  if (projectStore.defaultFont === null) {
    console.error('没有找到默认字体库')
  }

  return {
    isGroup: false,
    ...segment,
    ...segmentPositionSize,
    duration: TEXT_DURATION,
    ...DEFAULT_TEXT_PROPERTY(isSubtitleMode),
    material,
    type: SegmentType.Text,
    font: JSON.parse(JSON.stringify(projectStore.defaultFont)),
    font_id: projectStore.defaultFont ? projectStore.defaultFont.asset_id : 0,
    width: 674,
    height: DEFAULT_FONT_HEIGHT,
    anim_in: null,
    anim_out: null,
    anim_loop: null,
    anim_inout: null,
    is_replaceable: Bool.Falsely,
    speaker: '',
    stage: isSubtitleMode ? ProjectMode.Subtitle : segment.stage,
  }
}

export function resolveSegmentSubtitleGroup(): SegmentSubtitleGroup {
  const segment = resolveBaseSegment(SegmentType.Text)

  return {
    ...segment,
    duration: TEXT_DURATION,
    type: SegmentType.SubtitleGroup,
  }
}

export function resolveSegmentTransition(
  material: SegmentMaterial,
  before: SegmentTransition['before'],
  behind: SegmentTransition['behind'],
): SegmentTransition {
  const segment = resolveBaseSegment(material)

  const transition: SegmentTransition = {
    ...segment,
    material,
    type: SegmentType.Transition,
    // 外层已经限制了一半的时间 >= 0.1s，否则无法创建
    duration: TRANSITION_DURATION,
    before,
    behind,
    // 根据素材资源情况来确认
    overlap: getTransitionOverlap(material),
    visible: true,
    cuttingDuration: 0,
  }

  transition.duration = Math.min(
    TRANSITION_DURATION,
    getTransitionDurationMax(transition),
  )

  return transition
}

export function resolveSegmentFilter(material: SegmentMaterial): SegmentFilter {
  const segment = resolveBaseSegment(material)
  return {
    ...segment,
    material,
    type: SegmentType.Filter,
    duration: FILTER_DURATION,
    uni_alpha: FILTER_UNI_ALPHA,
  }
}

export function resolveSegmentSticker(
  material: SegmentMaterial,
): SegmentSticker {
  const segment = resolveBaseSegment(material)
  return {
    ...segment,
    ...segmentPositionSize,
    ...segmentMatting,
    material,
    type: SegmentType.Sticker,
    duration: STICKER_DURATION,
    anim_in: null,
    anim_out: null,
    anim_loop: null,
    anim_inout: null,
    is_replaceable: Bool.Falsely,
  }
}

export function resolveSegmentVideo(material: SegmentMaterial): SegmentVideo {
  const segment = resolveBaseSegment(material)

  return {
    isGroup: false,
    ...segment,
    ...segmentPositionSize,
    ...segmentMatting,
    duration:
      material.type === AssetType.Video ? segment.duration : IMAGE_DURATION,
    material,
    type: SegmentType.Video,
    asset_id: material.asset_id,
    audio_separatable: Boolean(material.out_mp3_url),
    audio_separated: false,
    volume: 0,
    fade_in_duration: 0,
    fade_out_duration: 0,
    anim_in: null,
    anim_out: null,
    anim_inout: null,
    anim_loop: null,
    preserves_pitch: true,
    before_transition_id: null,
    behind_transition_id: null,
    is_replaceable: Bool.Falsely,
    is_main_video: false,
    mute: false,
    speaker: '',
  }
}

export function resolveSegmentAudio(material: SegmentMaterial): SegmentAudio {
  const segment = resolveBaseSegment(material)

  return {
    isGroup: false,
    ...segment,
    material,
    type: SegmentType.Audio,
    volume: 0,
    fade_in_duration: 0,
    fade_out_duration: 0,
    preserves_pitch: true,
    is_replaceable: Bool.Falsely,
    mute: false,
    speaker: '',
    // 用来记录生成音频的文本内容
    text: '',
  }
}

export function resolveSegmentFragment(): SegmentFragment {
  const segment = resolveBaseSegment(SegmentType.Fragment)

  return {
    ...segment,
    type: SegmentType.Fragment,
    fragment_id: 0,
    duration: Fragment_DURATION,
    before_transition_id: null,
    behind_transition_id: null,
  }
}

export function resolveSegmentGroup(
  child: SegmentGroupCollection | SegmentNoGroupCollection,
): SegmentGroupCollection {
  // 默认获取最后一个
  const segment = cloneDeep(child)

  segment.isGroup = true
  if (isSupportDriftSpeed(segment)) {
    segment.drift_speed = 0
  }

  const material_duration =
    segment.rate === 1
      ? segment.material?.duration ?? segment.duration
      : segment.source_rate.material_duration

  const trim_start_time =
    segment.rate === 1
      ? segment.trim_start_time
      : segment.source_rate.trim_start_time

  const duration =
    segment.rate === 1 ? segment.duration : segment.source_rate.duration

  defineSegmentProperties(segment, {
    material_duration,
    trim_start_time,
    duration,
  })

  if (segment.type !== SegmentType.Audio && child.type !== SegmentType.Audio) {
    segment.width = child.width
    segment.height = child.height
  }

  return segment as SegmentGroupCollection
}

export function resolveSegmentProperties<T extends SegmentCollection>(
  segment: T,
): T {
  defineSegmentProperties(segment, {
    material_duration: hasSegmentMaterial(segment)
      ? segment.material.duration ?? segment.duration
      : segment.duration,
    trim_start_time: segment.trim_start_time,
    duration: segment.duration,
  })
  return segment
}

export function defineSegmentProperties<T extends SegmentCollection>(
  segment: T,
  source: Segment['source_rate'],
): T {
  source = segment.source_rate = {
    ...source,
    material_duration: getFullFrameMs(source.material_duration, {
      rounding: 'floor',
    }),
  }

  if (hasSegmentMaterial(segment) && segment.material.duration) {
    Object.defineProperty(segment.material, 'duration', {
      // 资源时长（根据变速计算）
      get(): number {
        const material_duration = getFullFrameMs(source.material_duration, {
          rounding: 'floor',
        })
        return getFullFrameMs(Math.floor(material_duration / segment.rate), {
          rounding: 'ceil',
        })
      },
      set() {
        throw new Error(`元件的总时长不能进行设置`)
      },
    })
  }

  defineSegmentFrameProperty(segment, 'trim_start_frame')
  defineSegmentFrameProperty(segment, 'start_frame')
  defineSegmentFrameProperty(segment, 'duration_frames')

  if (isSupportRate(segment)) {
    defineSegmentRateProperty(segment)
  }

  if (isSupportMoreRatio(segment)) {
    // 旧数据处理
    resolveSegmentRatios(segment)
    ratioEffectKeys.forEach((key) => {
      defineSegmentRadioProperty(segment, key)
    })
  }

  if (isSegmentFragment(segment)) {
    Object.defineProperty(segment, 'duration_frames', {
      get() {
        const trackStore = useTrackStore()

        const mainTrack = trackStore.getMainTrack(
          trackStore.getTracksByFragmentSegmentId(segment.id),
        )

        const segments = mainTrack?.segments ?? []

        if (segments.length === 0) {
          return durationToFrames(Fragment_DURATION)
        }

        return segments
          .filter((s) => !isTransition(s))
          .reduce(
            (acc, cur) => add(acc, getExcludeTransitionDurationFrames(cur)),
            0,
          )
      },
      set() {
        //
      },
    })
    Object.defineProperty(segment, 'duration', {
      get() {
        return framesToDuration(segment.duration_frames)
      },
      set() {
        //
      },
    })
  }

  defineSegmentBehavior(segment)

  return segment
}

const timeFrameMap: Record<
  keyof SegmentFrame,
  keyof Pick<Segment, 'duration' | 'trim_start_time' | 'start_time'>
> = {
  duration_frames: 'duration',
  trim_start_frame: 'trim_start_time',
  start_frame: 'start_time',
}
const sourceRateFrameMap: Record<
  keyof Pick<SegmentFrame, 'duration_frames' | 'trim_start_frame'>,
  keyof SegmentSourceRate
> = {
  duration_frames: 'duration',
  trim_start_frame: 'trim_start_time',
}

function defineSegmentFrameProperty<T extends SegmentCollection>(
  segment: T,
  key: keyof SegmentFrame,
): T {
  // 0 或者 不存在都可以判定为旧数据
  const value =
    segment[key] || durationToFrames(segment[timeFrameMap[key]], false)

  // 旧数据判断问题：因为任何视频资源都不可能帧数为0，如果duration_frames=0，则是旧数据。
  // const isOld = segment.duration_frames === 0
  // let value = isOld
  //   ? durationToFrames(segment[timeFrameMap[key]], false)
  //   : segment[key] ?? 0

  const __frame_key = `__${key}`
  // @ts-expect-error
  segment[__frame_key] = value

  if (isTransition(segment) && key === 'start_frame') {
    // review: 验证了frame getter setter 不影响到转场
    Object.defineProperty(segment, 'start_frame', {
      get() {
        const trackStore = useTrackStore()
        // 兼容了关系关联之前，前后视频元件都为空的情况
        if (!segment.before || !segment.behind) {
          return 0
        }
        const beforeEndFrame =
          segment.before.start_frame + segment.before.duration_frames
        if (trackStore.isCutting(segment)) {
          const offset =
            (durationToFrames(segment.cuttingDuration) -
              segment.duration_frames) /
            2
          const value = segment.overlap
            ? beforeEndFrame -
              durationToFrames(segment.cuttingDuration) +
              offset
            : beforeEndFrame - durationToFrames(segment.cuttingDuration) / 2
          return Math.floor(value)
        }

        const value = segment.overlap
          ? beforeEndFrame - segment.duration_frames
          : beforeEndFrame - segment.duration_frames / 2

        return Math.floor(value)
      },
      set(v) {
        //
      },
    })
  } else {
    Object.defineProperty(segment, key, {
      get() {
        // 兼容旧数据逻辑
        // @ts-expect-error
        return segment[__frame_key]
      },
      set(frames: number) {
        // trigger time prop setter
        segment[timeFrameMap[key]] = framesToDuration(frames)
      },
    })
  }

  const isRatableFrameProperties = (
    k: any,
  ): k is keyof Pick<SegmentFrame, 'duration_frames' | 'trim_start_frame'> => {
    const ratableProperties: (keyof Pick<
      SegmentFrame,
      'duration_frames' | 'trim_start_frame'
    >)[] = ['duration_frames', 'trim_start_frame']
    return ratableProperties.includes(k)
  }

  const source = segment.source_rate
  Object.defineProperty(segment, timeFrameMap[key], {
    get() {
      return framesToDuration(segment[key])
    },
    set(timeValue: number) {
      // @ts-expect-error
      segment[__frame_key] = durationToFrames(timeValue, false)

      // 变速字段相关处理
      if (isRatableFrameProperties(key)) {
        if (segment.rate === 1) {
          source[sourceRateFrameMap[key]] = timeValue
        } else if (hasSegmentMaterial(segment) && segment.material.duration) {
          // 保证一倍速时长是帧
          source[sourceRateFrameMap[key]] = Math.min(
            getFullFrameMs(
              (timeValue / segment.material.duration) *
                source.material_duration,
            ),
            source.material_duration,
          )
        } else {
          source[sourceRateFrameMap[key]] = getFullFrameMs(
            timeValue * segment.rate,
          )
        }
        source[sourceRateFrameMap[key]] = Math.floor(
          source[sourceRateFrameMap[key]],
        )
      }
    },
  })

  return segment
}

function defineSegmentRateProperty<T extends SegmentRateCollection>(
  segment: T,
): T {
  let rate = segment.rate

  Object.defineProperty(segment, 'rate', {
    get() {
      return rate
    },
    set(value) {
      // 需求：用户手动调整变速的时候，前端限制变速值最大不超过16。实际不限制。这样配音自动变速就不受影响了
      const max = isSegmentDubbing(segment)
        ? Infinity
        : getSegmentRateMax(segment)
      rate = Math.min(value, max)

      const s = segment
      // 帧化之前
      const beforeFramedRatedDuration = Math.floor(
        s.source_rate.duration / s.rate,
      )
      const ratedDuration = getFullFrameMs(beforeFramedRatedDuration)

      let ratedTrimStartTime = getFullFrameMs(
        Math.floor(s.source_rate.trim_start_time / s.rate),
        {
          rounding:
            beforeFramedRatedDuration > ratedDuration ? 'ceil' : 'floor',
        },
      )

      // 验证两个变速后加起来是不是超出了
      const ratedDurationFrames = durationToFrames(ratedDuration)
      const ratedTrimStartTimeFrames = durationToFrames(ratedTrimStartTime)
      const maxDurationFrames = durationToFrames(s.material.duration)
      if (
        add(ratedDurationFrames, ratedTrimStartTimeFrames) > maxDurationFrames
      ) {
        ratedTrimStartTime = framesToDuration(
          maxDurationFrames - ratedDurationFrames,
        )
      }
      console.warn('设置 trim_start_time 逻辑', ratedTrimStartTime)
      s.trim_start_time = ratedTrimStartTime
      s.duration = ratedDuration

      // 变速同步给子元件的逻辑
      if (isSegmentGroup(segment)) {
        const children = getGroupChildSegments(segment)
        children.forEach((child) => {
          child.rate = rate
        })
      }
    },
  })

  return segment
}

// 判断资源类型是否是图片（动图/静图）
export function isImage(type: AssetType): boolean {
  return [AssetType.Animation, AssetType.Image].includes(type)
}

/**
 * 设置多比例的属性时（位置、大小），修改到相应radio的属性
 * @param segment
 * @param key
 * @returns
 */
export function defineSegmentRadioProperty<
  T extends SegmentVideo | SegmentText | SegmentSticker,
>(segment: T, key: keyof RatioProperties): T {
  const projectStore = useProjectStore()
  const trackStore = useTrackStore()

  const getRatio = () => {
    if (projectStore.isReplaceProject) {
      return projectStore.ratio
    }

    if (trackStore.selectedTemplate && !isTemplateSegment(segment)) {
      return projectStore.configs.mainRatio
    }

    return projectStore.ratio
  }

  Object.defineProperty(segment, key, {
    get() {
      // 如果是多语言的文本元件，使用所属原语言的数据
      if (isLangSegment(segment) && segment.type === SegmentType.Text) {
        const source = trackStore.allUnLangSegments.find(
          (s) => s.id === segment.source_segment_id && s.type === segment.type,
        ) as T

        return source?.ratios?.[getRatio()]?.[key] ?? 0
      }
      return segment.ratios?.[getRatio()]?.[key] ?? 0
    },
    set(value: number) {
      let ratioProperties = segment.ratios?.[projectStore.ratio]
      // 设置多语言元件的属性时，修改的是所属原语言元件的对应属性
      if (isLangSegment(segment) && segment.type === SegmentType.Text) {
        const source = trackStore.allUnLangSegments.find(
          (s) => s.id === segment.source_segment_id && s.type === segment.type,
        ) as T

        if (source && isSegmentGroupChild(source)) {
          ratioProperties =
            getGroupBySegment(source)?.ratios?.[projectStore.ratio]
        } else {
          ratioProperties = source?.ratios?.[projectStore.ratio]
        }
      }
      if (ratioProperties) {
        // @ts-ignore
        ratioProperties[key] = value
      }
    },
  })

  return segment
}

// 处理没有ratios属性的数据
export function resolveSegmentRatios<
  T extends SegmentVideo | SegmentText | SegmentSticker,
>(segment: T): T {
  const projectStore = useProjectStore()
  const trackStore = useTrackStore()

  if (!segment.ratios) {
    const ratios: SegmentRatios = {}

    projectStore.ratios.forEach((ratio) => {
      const data: RatioProperties = resolveRatioProperty()

      ratioEffectKeys.forEach((key) => {
        // @ts-ignore
        data[key] = segment[key]
      })
      ratios[ratio] = data
    })

    Vue.set(segment, 'ratios', ratios)
  }

  // 有些场景下，可能会出现没有ratio不全的情况
  if (segment.ratios) {
    projectStore.ratios.forEach((ratio) => {
      if (segment.ratios && !segment.ratios[ratio]) {
        const data: RatioProperties = resolveRatioProperty()

        ratioEffectKeys.forEach((key) => {
          // @ts-ignore
          data[key] = segment[key]
        })
        Vue.set(segment.ratios, ratio, data)
      }
    })
  }

  // 如果是多语言的文本元件，使用所属原语言的数据
  if (isLangSegment(segment) && segment.type === SegmentType.Text) {
    Object.defineProperty(segment, 'ratios', {
      get() {
        const source = trackStore.allUnLangSegments.find(
          (s) => s.id === segment.source_segment_id && s.type === segment.type,
        )
        // @ts-ignore
        return source?.ratios
      },
      set() {
        //
      },
    })
  }

  return segment
}

export function hasSegmentMaterial(
  segment: SegmentCollection,
): segment is Exclude<
  SegmentCollection,
  SegmentText | SegmentSubtitleGroup | SegmentFragment
> {
  return !(
    segment.type === SegmentType.Text ||
    segment.type === SegmentType.SubtitleGroup ||
    segment.type === SegmentType.Fragment
  )
}

/**
 * 获取元件指定帧属性的值
 * @deprecated 为了兼容旧数据并没有帧值的case（0或者不存在都为没有值）
 * @param segment 元件
 * @param key 指定的属性
 * @returns 指定的属性的值
 */
export function getSegmentRealValue<K extends keyof SegmentFrame>(
  segment: Segment,
  key: K,
): Segment[K] {
  const frameValue = segment[key]
  const isNum = typeof frameValue === 'number'

  if (isNum && frameValue > 0) {
    return frameValue
  } else if (isNum && frameValue === 0 && segment[timeFrameMap[key]] > 0) {
    return durationToFrames(segment[timeFrameMap[key]])
  }

  // 1.isNum===false
  // 2.frameValue和segment[timeFrameMap[key]]都为0
  return 0
}
